import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, PackagePlus } from "lucide-react";
import { api } from "@/lib/api";
import type { ChannelPlatform, BrandSummary } from "@/lib/types";
import { DataTable, type DataColumn } from "@/components/dashboard/DataTable";
import { useAuth } from "@/auth/AuthContext";
// Hidden for now (2026-06-22): "Top actions across all brands" triage panel. Restore by uncommenting (here + render site below).
// import { PortfolioTriagePanel } from "@/components/portfolio/PortfolioTriagePanel";
import { PortfolioDeliverabilityPanel } from "@/components/portfolio/PortfolioDeliverabilityPanel";
import { EmptyState } from "@/components/dashboard/EmptyState";

const CHANNEL_LABEL: Record<ChannelPlatform, string> = {
  klaviyo: "Klaviyo",
  shopify: "Shopify",
  postscript: "Postscript",
  attentive: "Attentive",
  yotpo: "Yotpo",
  gorgias: "Gorgias",
  wshop: "WShop",
};

const CHANNEL_TINT: Record<ChannelPlatform, string> = {
  klaviyo: "bg-channel-klaviyo-bg text-channel-klaviyo",
  shopify: "bg-bg-subtle text-text-secondary",
  postscript: "bg-channel-sms-bg text-channel-sms",
  attentive: "bg-channel-sms-bg text-channel-sms",
  yotpo: "bg-bg-subtle text-text-secondary",
  gorgias: "bg-bg-subtle text-text-secondary",
  wshop: "bg-bg-subtle text-text-secondary",
};

function ChannelTags({ channels }: { channels: ChannelPlatform[] }) {
  if (channels.length === 0) {
    return <span className="text-text-dim italic">No channels yet</span>;
  }
  return (
    <div className="flex gap-1 flex-wrap">
      {channels.map((c) => (
        <span key={c} className={`px-2 py-0.5 text-xs rounded ${CHANNEL_TINT[c]}`}>
          {CHANNEL_LABEL[c]}
        </span>
      ))}
    </div>
  );
}

function PendingCell({ count }: { count: number }) {
  return count > 0 ? (
    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold bg-priority-high-bg text-priority-high tabular-nums">
      {count}
    </span>
  ) : (
    <span className="text-text-dim tabular-nums">—</span>
  );
}

export function Portfolio() {
  const { me } = useAuth();
  const [params] = useSearchParams();
  // SuperAdmin cross-org view (opt-in via ?scope=all, reached from the OrgPicker).
  const allOrgs = params.get("scope") === "all";

  const brandsQuery = useQuery({
    queryKey: ["brands", allOrgs ? "all" : "mine"],
    queryFn: allOrgs ? api.listBrandsAcrossOrgs : api.listBrands,
  });

  // Cross-org drill-in: switch the active org (rotates JWT so RLS scope is
  // correct) BEFORE navigating into the brand. Hard-nav so the new cookie is
  // picked up. Falls through to plain nav if the switch fails.
  async function openCrossOrgBrand(b: BrandSummary) {
    if (b.org_id) {
      try {
        await api.setActiveOrg(b.org_id);
      } catch {
        /* fall through to navigation */
      }
    }
    window.location.href = `/brand/${b.slug}/dashboard`;
  }

  const brands = brandsQuery.data ?? [];
  const orgCount = allOrgs
    ? new Set(brands.map((b) => b.org_id ?? "")).size
    : 1;

  // Group brands by org for the cross-org view (preserves backend ordering).
  const groups: { orgName: string; brands: BrandSummary[] }[] = [];
  if (allOrgs) {
    const byOrg = new Map<string, BrandSummary[]>();
    for (const b of brands) {
      const key = b.org_name ?? "—";
      if (!byOrg.has(key)) byOrg.set(key, []);
      byOrg.get(key)!.push(b);
    }
    for (const [orgName, list] of byOrg) groups.push({ orgName, brands: list });
  }

  // Shared column set for both the single-org and per-org-group tables. The
  // single-org table links each brand to its dashboard; the cross-org tables
  // route through openCrossOrgBrand (via onRowClick) so the JWT org scope
  // rotates first — there the name is plain text, not a Link.
  const singleOrgColumns: DataColumn<BrandSummary>[] = [
    {
      key: "name",
      header: "Brand",
      cell: (b) => (
        <Link
          to={`/brand/${b.slug}/dashboard`}
          className="font-medium text-text-primary hover:text-accent"
        >
          {b.name}
        </Link>
      ),
      sortValue: (b) => b.name,
    },
    {
      key: "channels",
      header: "Channels",
      cell: (b) => <ChannelTags channels={b.channels} />,
    },
    {
      key: "pending",
      header: "Pending actions",
      align: "right",
      cell: (b) => <PendingCell count={b.pending_action_count} />,
      sortValue: (b) => b.pending_action_count,
    },
  ];

  const crossOrgColumns: DataColumn<BrandSummary>[] = [
    {
      key: "name",
      header: "Brand",
      cell: (b) => (
        <span className="font-medium text-text-primary">{b.name}</span>
      ),
      sortValue: (b) => b.name,
    },
    {
      key: "channels",
      header: "Channels",
      cell: (b) => <ChannelTags channels={b.channels} />,
    },
    {
      key: "pending",
      header: "Pending actions",
      align: "right",
      cell: (b) => <PendingCell count={b.pending_action_count} />,
      sortValue: (b) => b.pending_action_count,
    },
  ];

  return (
    <div className="px-8 py-6 max-w-6xl mx-auto space-y-5">
      <header>
        <h1 className="text-2xl font-bold tracking-tight text-text-primary">
          {allOrgs
            ? "All organisations"
            : `Welcome${me?.user.display_name ? `, ${me.user.display_name}` : ""}`}
        </h1>
        <p className="text-text-muted text-sm mt-1">
          {allOrgs
            ? `${brands.length} brand${brands.length === 1 ? "" : "s"} across ${orgCount} organisation${orgCount === 1 ? "" : "s"}.`
            : `${me?.brands.length ?? 0} brand${me && me.brands.length === 1 ? "" : "s"} in your org.`}
        </p>
      </header>

      {brandsQuery.isLoading && (
        <div className="flex items-center gap-2 text-text-muted py-12 justify-center">
          <Loader2 className="animate-spin" size={16} /> Loading brands…
        </div>
      )}

      {/* Org-scoped panels only make sense for a single active org. */}
      {!allOrgs && <PortfolioDeliverabilityPanel />}
      {/* Hidden for now (2026-06-22): "Top actions across all brands" triage panel. Restore by uncommenting. */}
      {/* {!allOrgs && <PortfolioTriagePanel />} */}

      {brandsQuery.error && (
        <div className="rounded-2xl bg-priority-critical-bg p-4 text-priority-critical text-sm shadow-mx">
          Couldn't load brands. Try again later.
        </div>
      )}

      {brandsQuery.data && brands.length === 0 && (
        <EmptyState
          icon={PackagePlus}
          title="No brands yet"
          body="Add your first brand to get started."
          action={
            <Link
              to="/onboard"
              className="inline-flex h-9 items-center justify-center rounded-md bg-gradient-to-r from-[#B45309] to-[#D97706] px-5 text-sm font-semibold text-white shadow-primary transition-all hover:brightness-95 active:brightness-90"
            >
              Add a brand
            </Link>
          }
        />
      )}

      {/* Single-org table */}
      {!allOrgs && brandsQuery.data && brands.length > 0 && (
        <DataTable
          rows={brands}
          rowKey={(b) => b.id}
          rank
          defaultSort={{ key: "pending", dir: "desc" }}
          columns={singleOrgColumns}
          emptyText="No brands in your org."
          pageSize={10}
          pageNoun="brands"
        />
      )}

      {/* Cross-org grouped tables — one soft card per org. */}
      {allOrgs && brandsQuery.data && brands.length > 0 && (
        <div className="space-y-6">
          {groups.map((g) => (
            <div key={g.orgName} className="space-y-2">
              <div className="px-1 text-[11px] uppercase tracking-[0.08em] font-semibold text-text-muted">
                {g.orgName}
              </div>
              <DataTable
                rows={g.brands}
                rowKey={(b) => b.id}
                columns={crossOrgColumns}
                onRowClick={(b) => void openCrossOrgBrand(b)}
                emptyText="No brands in this organisation."
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
